import { useState, useEffect, useRef } from "react";
import { FaSun, FaTint } from "react-icons/fa";
import { IoAdd, IoBusiness, IoChevronDown, IoCheckmarkCircle, IoClose, IoKey, IoLeaf, IoPerson } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import logoImg from "../assets/logo-white.png";
import { useAuth } from "../contexts/AuthContext";
import { apiPreAuth } from "../services/api";

const mensagemDeErro = (err, fallback) => {
  if (!err.response) {
    return "Não foi possível conectar ao servidor. Verifique sua internet ou se o backend está no ar.";
  }
  return err.response?.data?.detail || fallback;
};

// Formata como CPF enquanto ≤ 11 dígitos são digitados, muda pra máscara de
// CNPJ a partir do 12º dígito — mesmo campo aceita os dois documentos sem
// exigir que a pessoa escolha um tipo antes de digitar.
const CPF_CNPJ_MASK = (v) => {
  const digitos = v.replace(/\D/g, "").slice(0, 14);
  if (digitos.length <= 11) {
    return digitos
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1-$2");
  }
  return digitos
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};

const EMPRESAS_LEMBRADAS_KEY = "empresasLembradas";
const MAX_EMPRESAS_LEMBRADAS = 5;

// Lê a lista de empresas já logadas nesse navegador. Se só existir o formato
// antigo (uma credencial só, de antes desta lista existir), migra pro
// formato novo — sem razão social, a entrada migrada aparece com o
// documento formatado no lugar do nome até a pessoa logar de novo.
const carregarEmpresasLembradas = () => {
  try {
    const bruto = localStorage.getItem(EMPRESAS_LEMBRADAS_KEY);
    if (bruto) {
      const lista = JSON.parse(bruto);
      if (Array.isArray(lista)) return lista;
    }
  } catch (erro) {
    console.error("Erro ao carregar empresas lembradas:", erro);
  }

  try {
    const antigo = localStorage.getItem("savedCredentials");
    if (antigo) {
      const credenciais = JSON.parse(antigo);
      const digitos = (credenciais.cnpj || "").replace(/\D/g, "");
      localStorage.removeItem("savedCredentials");
      if (digitos) {
        const migrada = [{ cnpj: digitos, razaoSocial: "", username: credenciais.username || "" }];
        localStorage.setItem(EMPRESAS_LEMBRADAS_KEY, JSON.stringify(migrada));
        return migrada;
      }
    }
  } catch (erro) {
    console.error("Erro ao migrar credenciais salvas:", erro);
  }
  return [];
};

// Rótulo do <option>: nome da empresa, ou o documento formatado se ainda não
// sabemos o nome (entrada migrada do formato antigo). Completa com o
// documento entre parênteses quando duas empresas lembradas têm o mesmo nome.
const rotularEmpresaLembrada = (empresa, todas) => {
  const nome = empresa.razaoSocial?.trim();
  if (!nome) return CPF_CNPJ_MASK(empresa.cnpj);
  const duplicado = todas.filter((e) => e.razaoSocial?.trim() === nome).length > 1;
  return duplicado ? `${nome} (${CPF_CNPJ_MASK(empresa.cnpj)})` : nome;
};

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [step, setStep] = useState("credenciais");
  const [cnpj, setCnpj] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [empresasLembradas, setEmpresasLembradas] = useState([]);
  const [novaEmpresa, setNovaEmpresa] = useState(false);
  const [dropdownAberto, setDropdownAberto] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [fazendas, setFazendas] = useState([]);
  const [fazendaSelecionada, setFazendaSelecionada] = useState(null);
  const [focusedField, setFocusedField] = useState(null);
  const dropdownRef = useRef(null);

  // Campo de CNPJ/CPF vira um dropdown assim que existe ao menos uma empresa
  // lembrada nesse navegador — só volta a ser texto livre se a pessoa
  // escolher "Nova empresa" dentro dele.
  const modoSelect = empresasLembradas.length > 0 && !novaEmpresa;
  const cnpjAtualDigitos = cnpj.replace(/\D/g, "");
  const empresaAtual =
    empresasLembradas.find((empresa) => empresa.cnpj === cnpjAtualDigitos) || empresasLembradas[0];

  // Fecha o dropdown ao clicar fora dele ou apertar Esc.
  useEffect(() => {
    if (!dropdownAberto) return undefined;
    const aoClicarFora = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownAberto(false);
      }
    };
    const aoTeclarEscape = (e) => {
      if (e.key === "Escape") setDropdownAberto(false);
    };
    document.addEventListener("mousedown", aoClicarFora);
    document.addEventListener("keydown", aoTeclarEscape);
    return () => {
      document.removeEventListener("mousedown", aoClicarFora);
      document.removeEventListener("keydown", aoTeclarEscape);
    };
  }, [dropdownAberto]);

  // Carregar empresas lembradas ao montar o componente
  useEffect(() => {
    const lista = carregarEmpresasLembradas();
    if (lista.length > 0) {
      setEmpresasLembradas(lista);
      setRememberMe(true);
      setCnpj(CPF_CNPJ_MASK(lista[0].cnpj));
      setUsername(lista[0].username || "");
    }
  }, []);

  const handleRememberMeChange = () => setRememberMe((atual) => !atual);

  // Nunca guarda a senha em localStorage — diferente do token de sessão (que
  // expira e pode ser revogado), uma senha em texto puro seria a credencial
  // permanente da conta: qualquer XSS futuro em qualquer parte do app
  // conseguiria roubá-la. "Lembrar-me" só evita redigitar CNPJ/CPF e usuário.
  const salvarEmpresaAposLogin = (cnpjDigitos, razaoSocial, usernameAtual) => {
    if (!rememberMe || !cnpjDigitos) return;
    setEmpresasLembradas((atual) => {
      const semDuplicata = atual.filter((empresa) => empresa.cnpj !== cnpjDigitos);
      const nova = [
        { cnpj: cnpjDigitos, razaoSocial: razaoSocial || "", username: usernameAtual },
        ...semDuplicata,
      ].slice(0, MAX_EMPRESAS_LEMBRADAS);
      localStorage.setItem(EMPRESAS_LEMBRADAS_KEY, JSON.stringify(nova));
      return nova;
    });
  };

  const selecionarEmpresa = (empresa) => {
    setCnpj(CPF_CNPJ_MASK(empresa.cnpj));
    setUsername(empresa.username || "");
    setDropdownAberto(false);
  };

  const handleNovaEmpresa = () => {
    setNovaEmpresa(true);
    setCnpj("");
    setUsername("");
    setDropdownAberto(false);
  };

  const handleVoltarParaEmpresaLembrada = () => {
    setNovaEmpresa(false);
    const primeira = empresasLembradas[0];
    if (primeira) {
      setCnpj(CPF_CNPJ_MASK(primeira.cnpj));
      setUsername(primeira.username || "");
    }
  };

  // Higiene em computador compartilhado: remove só a empresa clicada, sem
  // mexer nas demais lembradas nesse navegador. Não fecha o dropdown, pra
  // dar pra remover mais de uma em sequência.
  const handleEsquecerEmpresa = (empresa, e) => {
    e.stopPropagation();
    const cnpjDigitos = empresa.cnpj;
    setEmpresasLembradas((atual) => {
      const nova = atual.filter((item) => item.cnpj !== cnpjDigitos);
      if (nova.length > 0) {
        localStorage.setItem(EMPRESAS_LEMBRADAS_KEY, JSON.stringify(nova));
        if (cnpjAtualDigitos === cnpjDigitos) {
          setCnpj(CPF_CNPJ_MASK(nova[0].cnpj));
          setUsername(nova[0].username || "");
        }
      } else {
        localStorage.removeItem(EMPRESAS_LEMBRADAS_KEY);
        setCnpj("");
        setUsername("");
        setDropdownAberto(false);
      }
      return nova;
    });
  };

  const handleCredenciais = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await apiPreAuth.post("/api/auth/login/", {
        cnpj,
        username,
        password,
      });
      const cnpjDigitos = cnpj.replace(/\D/g, "");

      // Usuário admin auto-criado ao cadastrar a empresa (sem fazenda ainda)
      // — o backend pula direto pro token completo e manda pra tela de
      // configuração inicial, sem passar por seleção de fazenda.
      if (response.data.admin_empresa) {
        const { access, refresh, razao_social } = response.data;
        localStorage.setItem("token", access);
        localStorage.setItem("refresh_token", refresh);
        salvarEmpresaAposLogin(cnpjDigitos, razao_social, username);
        navigate("/configuracao-inicial");
        return;
      }

      const { pre_token, fazendas: listaFazendas, user, razao_social } = response.data;

      localStorage.setItem("pre_token", pre_token);
      localStorage.setItem("pre_user", JSON.stringify(user));

      // Salva a empresa na lista lembrada se "Lembrar-me" estiver marcado
      salvarEmpresaAposLogin(cnpjDigitos, razao_social, username);

      if (listaFazendas.length === 1) {
        await selecionarFazenda(listaFazendas[0].id);
      } else {
        setFazendas(listaFazendas);
        setStep("fazenda");
      }
    } catch (err) {
      console.error("ERRO LOGIN:", err);
      setError(mensagemDeErro(err, "Credenciais inválidas ou CNPJ/CPF incorreto."));
    } finally {
      setLoading(false);
    }
  };

  const selecionarFazenda = async (fazendaId) => {
    setLoading(true);
    setError("");
    try {
      const response = await apiPreAuth.post("/api/auth/selecionar-fazenda/", {
        fazenda_id: fazendaId,
      });
      const { access, refresh, user, fazenda, cargo, permissoes, must_change_password } = response.data;

      login(user, access, fazenda, permissoes, cargo, refresh, must_change_password);

      localStorage.removeItem("pre_token");
      localStorage.removeItem("pre_user");

      navigate("/dashboard");
    } catch (err) {
      console.error("ERRO SELECIONAR FAZENDA:", err);
      setError(mensagemDeErro(err, "Erro ao selecionar fazenda."));
    } finally {
      setLoading(false);
    }
  };

  const handleSelecionarFazenda = async (e) => {
    e.preventDefault();
    if (!fazendaSelecionada) {
      setError("Selecione uma fazenda.");
      return;
    }
    await selecionarFazenda(fazendaSelecionada);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-green-100 rounded-full opacity-20 blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-100 rounded-full opacity-20 blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-50 rounded-full opacity-10 blur-3xl" />
      </div>

      {/* Floating icons */}
      <div className="absolute top-20 left-10 opacity-10 animate-float text-6xl">
        <IoLeaf size={48} />
      </div>
      <div className="absolute top-1/3 right-20 opacity-5 animate-float text-4xl">
        <FaSun size={48} />
      </div>
      <div className="absolute bottom-1/3 left-20 opacity-5 animate-float-delayed text-4xl">
        <FaTint />
      </div>

      {/* Main Card */}
      <div className="w-full max-w-6xl relative z-10">
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
          <div className="flex flex-col lg:flex-row">
            {/* Left Side - Branding */}
            <div
              className="w-full lg:w-5/12 p-8 lg:p-10 text-white relative overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #1a472a 10%, #0d2818 90%)",
              }}
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-yellow-500/5 rounded-full blur-2xl" />
              <div className="relative z-10 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-8">
                  <div>
                    <div className="flex items-center mb-2">
                      <img
                        src={logoImg}
                        alt="Logo Novalis Agro"
                        className="h-20 w-auto"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex-1 flex flex-col justify-center space-y-6">
                  <div className="space-y-2">
                    <div className="w-16 h-1 bg-emerald-950 rounded-full mb-4" />
                    <h1 className="text-4xl lg:text-5xl font-bold leading-tight">
                      {step === "credenciais"
                        ? "Bem-vindo!"
                        : "Selecione a fazenda"}
                    </h1>
                    <p className="text-emerald-100 text-lg mt-2">
                      {step === "credenciais"
                        ? "Acesse sua conta e gerencie suas operações agrícolas."
                        : "Escolha a fazenda que deseja gerenciar"}
                    </p>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-white/10">
                  <p className="text-emerald-200/60 text-xs">
                    © 2026 Novalis Agro • Todos os direitos reservados
                  </p>
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="w-full lg:w-7/12 p-8 lg:p-12">
              <div className="max-w-md mx-auto">
                {/* Logo mobile */}
                <div className="flex justify-center mb-8 lg:hidden">
                  <div className="bg-green-100 p-3 rounded-2xl text-3xl">
                    <IoLeaf size={24} />
                  </div>
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl animate-shake">
                    <p className="text-red-600 text-sm flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                      {error}
                    </p>
                  </div>
                )}

                {step === "credenciais" ? (
                  <form onSubmit={handleCredenciais} className="space-y-5">
                    {/* CNPJ/CPF Field — select quando já existe empresa lembrada, texto livre caso contrário */}
                    <div className="space-y-1.5">
                      <label className="block text-sm font-semibold text-gray-700">
                        CNPJ ou CPF da Empresa
                      </label>
                      {modoSelect ? (
                        <div className="relative" ref={dropdownRef}>
                          <button
                            type="button"
                            onClick={() => setDropdownAberto((v) => !v)}
                            className={`w-full pl-10 pr-10 py-3 border-2 rounded-xl transition-all outline-none bg-white text-left flex items-center ${
                              dropdownAberto
                                ? "border-green-500 ring-2 ring-green-200"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                              <IoBusiness size={20} />
                            </span>
                            <span className="truncate text-gray-800">
                              {empresaAtual ? rotularEmpresaLembrada(empresaAtual, empresasLembradas) : "Selecionar empresa"}
                            </span>
                            <span
                              className={`absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-transform duration-200 ${dropdownAberto ? "rotate-180" : ""}`}
                            >
                              <IoChevronDown size={18} />
                            </span>
                          </button>

                          {dropdownAberto && (
                            <div className="absolute z-20 mt-2 w-full bg-white border border-gray-100 rounded-xl shadow-xl overflow-hidden animate-dropdown">
                              <ul className="max-h-56 overflow-y-auto py-1">
                                {empresasLembradas.map((empresa) => {
                                  const selecionada = empresa.cnpj === cnpjAtualDigitos;
                                  const nome = rotularEmpresaLembrada(empresa, empresasLembradas);
                                  return (
                                    <li
                                      key={empresa.cnpj}
                                      onClick={() => selecionarEmpresa(empresa)}
                                      className={`group flex items-center justify-between gap-2 px-3 py-2.5 cursor-pointer transition-colors ${
                                        selecionada ? "bg-green-50" : "hover:bg-gray-50"
                                      }`}
                                    >
                                      <div className="flex items-center gap-2.5 min-w-0">
                                        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-green-100 text-green-700 text-xs font-semibold shrink-0">
                                          {nome.charAt(0).toUpperCase()}
                                        </span>
                                        <span className="truncate text-sm text-gray-700">{nome}</span>
                                      </div>
                                      <div className="flex items-center gap-1 shrink-0">
                                        {selecionada && <IoCheckmarkCircle className="text-green-600" size={16} />}
                                        <button
                                          type="button"
                                          onClick={(e) => handleEsquecerEmpresa(empresa, e)}
                                          title="Esquecer esta empresa"
                                          className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-md p-1 transition-all"
                                        >
                                          <IoClose size={14} />
                                        </button>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                              <button
                                type="button"
                                onClick={handleNovaEmpresa}
                                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-green-700 hover:bg-green-50 border-t border-gray-100 transition-colors"
                              >
                                <IoAdd size={16} />
                                Nova empresa
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <div
                            className={`relative transition-all duration-200 ${focusedField === "cnpj" ? "transform scale-[1.02]" : ""}`}
                          >
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                              <IoBusiness size={20} />
                            </span>
                            <input
                              type="text"
                              value={cnpj}
                              onFocus={() => setFocusedField("cnpj")}
                              onBlur={() => setFocusedField(null)}
                              onChange={(e) => setCnpj(CPF_CNPJ_MASK(e.target.value))}
                              className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                              placeholder="000.000.000-00 ou 00.000.000/0001-00"
                              required
                            />
                          </div>
                          {empresasLembradas.length > 0 && (
                            <button
                              type="button"
                              onClick={handleVoltarParaEmpresaLembrada}
                              className="text-xs text-green-700 hover:text-green-900 transition-colors"
                            >
                              ← Usar empresa já salva
                            </button>
                          )}
                        </>
                      )}
                    </div>

                    {/* Username Field */}
                    <div className="space-y-1.5">
                      <label className="block text-sm font-semibold text-gray-700">
                        Usuário
                      </label>
                      <div
                        className={`relative transition-all duration-200 ${focusedField === "username" ? "transform scale-[1.02]" : ""}`}
                      >
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                          <IoPerson size={20} />
                        </span>
                        <input
                          type="text"
                          value={username}
                          onFocus={() => setFocusedField("username")}
                          onBlur={() => setFocusedField(null)}
                          onChange={(e) => setUsername(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                          placeholder="Digite seu usuário"
                          required
                        />
                      </div>
                    </div>

                    {/* Password Field */}
                    <div className="space-y-1.5">
                      <label className="block text-sm font-semibold text-gray-700">
                        Senha
                      </label>
                      <div
                        className={`relative transition-all duration-200 ${focusedField === "password" ? "transform scale-[1.02]" : ""}`}
                      >
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                          <IoKey size={20} />
                        </span>
                        <input
                          type="password"
                          value={password}
                          onFocus={() => setFocusedField("password")}
                          onBlur={() => setFocusedField(null)}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                          placeholder="••••••••"
                          required
                        />
                      </div>
                    </div>

                    {/* Remember Me */}
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={rememberMe}
                            onChange={handleRememberMeChange}
                            className="sr-only peer"
                          />
                          <div
                            className={`w-5 h-5 border-2 rounded-md transition-all ${
                              rememberMe
                                ? "bg-green-600 border-green-600"
                                : "bg-white border-gray-300"
                            }`}
                          >
                            {rememberMe && (
                              <span className="flex items-center justify-center text-white text-xs">
                                ✓
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="text-sm text-gray-600">
                          Lembrar-me
                        </span>
                      </label>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full text-white font-bold py-3.5 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-lg disabled:opacity-60"
                      style={{
                        background:
                          "linear-gradient(135deg, #1a472a 0%, #0d2818 100%)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background =
                          "linear-gradient(135deg, #0d2818 0%, #051205 100%)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          "linear-gradient(135deg, #1a472a 0%, #0d2818 100%)";
                      }}
                    >
                      {loading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Verificando...</span>
                        </>
                      ) : (
                        <>
                          <span>Entrar</span>
                          <span>→</span>
                        </>
                      )}
                    </button>
                  </form>
                ) : (
                  <form
                    onSubmit={handleSelecionarFazenda}
                    className="space-y-5"
                  >
                    <div className="space-y-3 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
                      {fazendas.map((f) => (
                        <label
                          key={f.id}
                          className={`flex items-start gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                            fazendaSelecionada === f.id
                              ? "border-green-500 bg-gradient-to-r from-green-50 to-emerald-50 shadow-md"
                              : "border-gray-200 hover:border-green-300 hover:bg-gray-50"
                          }`}
                        >
                          <input
                            type="radio"
                            name="fazenda"
                            value={f.id}
                            checked={fazendaSelecionada === f.id}
                            onChange={() => setFazendaSelecionada(f.id)}
                            className="mt-1 accent-green-600 w-4 h-4"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-green-600">🏘️</span>
                              <p className="font-semibold text-gray-800">
                                {f.nome}
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-3 text-xs">
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full">
                                Cargo: {f.cargo}
                              </span>
                              {f.plano && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                                  Plano: {f.plano}
                                </span>
                              )}
                            </div>
                          </div>
                          {fazendaSelecionada === f.id && (
                            <span className="text-green-600 flex-shrink-0">
                              ✓
                            </span>
                          )}
                        </label>
                      ))}
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setStep("credenciais");
                          setError("");
                        }}
                        className="flex-1 border-2 border-gray-300 text-gray-700 font-semibold py-3 rounded-xl hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                      >
                        <span>←</span>
                        Voltar
                      </button>
                      <button
                        type="submit"
                        disabled={loading || !fazendaSelecionada}
                        className="flex-1 bg-gradient-to-r from-green-700 to-green-800 hover:from-green-800 hover:to-green-900 disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-green-200"
                      >
                        {loading ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <span>Acessar</span>
                            <span>→</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(20px); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 0.3; }
        }
        @keyframes dropdown {
          from { opacity: 0; transform: translateY(-6px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-dropdown {
          animation: dropdown 0.15s ease-out;
          transform-origin: top;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 7s ease-in-out infinite;
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }
        .animate-pulse {
          animation: pulse 4s ease-in-out infinite;
        }
        .delay-1000 {
          animation-delay: 1s;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </div>
  );
};

export default Login;
